import type { PropsWithChildren } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import * as Popover from '@radix-ui/react-popover'
import { space, tok, withStyleModifiers } from '@sunwebgroup/ui-components'
import { resetFocusOutline, Token, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { css, styled } from 'styled-components'
import { BaseInput } from '../BaseInput'
import { DropdownMultiselectContext } from './context'

const Chips = styled.div<{ $disabled: boolean | undefined }>`
  align-items: center;
  display: flex;
  min-width: 0;
  position: relative;

  > * {
    margin-right: ${space('primitives_space_4')};

    &:last-child {
      margin-right: 0;
    }
  }

  ${Token} {
    ${resetFocusOutline()}
  }

  ${({ $disabled }) =>
    $disabled
    && css`
      pointer-events: none;
    `}
`
const Placeholder = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  user-select: none;
  margin-right: ${space('primitives_space_24')};
  color: ${tok('semantics_input_fields_placeholder_color')};
`

function modDisabled() {
  return css`
  ${BaseInput.modDisabled()}
`
}

export const Selection = withStyleModifiers(styled(SelectionPrimitive)``, { modDisabled })

function SelectionPrimitive({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  const { selectedValues, disabled, placeholder, asDialog } = React.useContext(DropdownMultiselectContext)

  const content = (
    <BaseInput.Root className={className}>
      <BaseInput.Box tabIndex={disabled ? undefined : 0}>
        {selectedValues.length > 0
          ? (
              <>
                <BaseInput.Content>
                  <Chips $disabled={disabled}>{children}</Chips>
                </BaseInput.Content>
              </>
            )
          : (
              <>
                <Placeholder $variant="body-m-regular-tight">{placeholder}</Placeholder>
              </>
            )}
      </BaseInput.Box>
      <BaseInput.ChevronDown />
    </BaseInput.Root>
  )

  return asDialog
    ? (
        <Dialog.Trigger asChild>{content}</Dialog.Trigger>
      )
    : (
        <Popover.Trigger asChild>{content}</Popover.Trigger>
      )
}
